/** React Query hooks over `inventory-api.ts`: locations, conditions, copies, transactions. */

import { keepPreviousData, useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  inventoryApi,
  shelfLocationsApi,
  conditionsApi,
  bookCopiesApi,
  inventoryTransactionsApi,
  type ShelfLocation,
  type Condition,
  type BookCopy,
  type InventoryTransaction,
  type InventoryStats,
  type ShelfLocationQueryParams,
  type BookCopyQueryParams,
  type TransactionQueryParams,
  type CreateShelfLocationData,
  type UpdateShelfLocationData,
  type CreateConditionData,
  type UpdateConditionData,
  type CreateBookCopyData,
  type UpdateBookCopyData,
  type BulkLocationUpdateData,
  type BulkStatusUpdateData,
} from '@/services/inventory-api';
import { BaseQueryParams } from '@/services/api-base';

// Re-export types for convenience
export type {
  ShelfLocation,
  Condition,
  BookCopy,
  InventoryTransaction,
  InventoryStats,
  ShelfLocationQueryParams,
  BookCopyQueryParams,
  TransactionQueryParams,
  CreateShelfLocationData,
  UpdateShelfLocationData,
  CreateConditionData,
  UpdateConditionData,
  CreateBookCopyData,
  UpdateBookCopyData,
  BulkLocationUpdateData,
  BulkStatusUpdateData,
} from '@/services/inventory-api';

/** Query key factories */
export const inventoryQueryKeys = {
  // Shelf Locations
  shelfLocations: {
    all: ['shelf-locations'] as const,
    lists: () => [...inventoryQueryKeys.shelfLocations.all, 'list'] as const,
    list: (params?: ShelfLocationQueryParams) => [...inventoryQueryKeys.shelfLocations.lists(), params] as const,
    details: () => [...inventoryQueryKeys.shelfLocations.all, 'detail'] as const,
    detail: (id: number) => [...inventoryQueryKeys.shelfLocations.details(), id] as const,
    summary: () => [...inventoryQueryKeys.shelfLocations.all, 'summary'] as const,
    locationBooks: (id: number, params?: BookCopyQueryParams) => 
      [...inventoryQueryKeys.shelfLocations.detail(id), 'books', params] as const,
  },
  
  // Conditions
  conditions: {
    all: ['conditions'] as const,
    lists: () => [...inventoryQueryKeys.conditions.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...inventoryQueryKeys.conditions.lists(), params] as const,
    details: () => [...inventoryQueryKeys.conditions.all, 'detail'] as const,
    detail: (id: number) => [...inventoryQueryKeys.conditions.details(), id] as const,
    statistics: () => [...inventoryQueryKeys.conditions.all, 'statistics'] as const,
  },
  
  // Book Copies
  bookCopies: {
    all: ['book-copies'] as const,
    lists: () => [...inventoryQueryKeys.bookCopies.all, 'list'] as const,
    list: (params?: BookCopyQueryParams) => [...inventoryQueryKeys.bookCopies.lists(), params] as const,
    details: () => [...inventoryQueryKeys.bookCopies.all, 'detail'] as const,
    detail: (id: number) => [...inventoryQueryKeys.bookCopies.details(), id] as const,
    statistics: () => [...inventoryQueryKeys.bookCopies.all, 'statistics'] as const,
    transactionHistory: (id: number, params?: TransactionQueryParams) => 
      [...inventoryQueryKeys.bookCopies.detail(id), 'transactions', params] as const,
  },
  
  // Transactions
  transactions: {
    all: ['inventory-transactions'] as const,
    lists: () => [...inventoryQueryKeys.transactions.all, 'list'] as const,
    list: (params?: TransactionQueryParams) => [...inventoryQueryKeys.transactions.lists(), params] as const,
    details: () => [...inventoryQueryKeys.transactions.all, 'detail'] as const,
    detail: (id: number) => [...inventoryQueryKeys.transactions.details(), id] as const,
    activitySummary: (params?: { days?: number }) => 
      [...inventoryQueryKeys.transactions.all, 'activity-summary', params] as const,
  },
};

/**
 * Helper function to invalidate related queries after mutations
 */
const invalidateRelatedQueries = (queryClient: QueryClient, entityType: string, id?: number) => {
  switch (entityType) {
    case 'shelf-location':
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.shelfLocations.all });
      if (id) {
        queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.bookCopies.all });
      }
      break;
    case 'condition':
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.conditions.all });
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.bookCopies.all });
      break;
    case 'book-copy':
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.bookCopies.all });
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.transactions.all });
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.shelfLocations.all });
      break;
  }
};

/**
 * Error handler for mutations
 */
const handleMutationError = (error: AxiosError, operation: string) => {
  console.error(`Inventory ${operation} error:`, error);
  const errorData = error.response?.data as { detail?: string; message?: string } | undefined;
  const errorMessage = errorData?.detail || 
                      errorData?.message || 
                      `Failed to ${operation}`;
  toast.error(errorMessage);
};

/**
 * Success handler for mutations
 */
const handleMutationSuccess = (operation: string) => {
  toast.success(`Successfully ${operation}`);
};

// =============================================================================
// SHELF LOCATION HOOKS
// =============================================================================

/**
 * Hook to fetch shelf locations with optional filtering
 */
export const useShelfLocations = (params?: ShelfLocationQueryParams) => {
  return useQuery({
    queryKey: inventoryQueryKeys.shelfLocations.list(params),
    queryFn: () => shelfLocationsApi.getAll(params).then(res => res.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Paging and filtering keep the current rows on screen until the next page lands.
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch a specific shelf location by ID
 */
export const useShelfLocation = (id: number, enabled = true) => {
  return useQuery({
    queryKey: inventoryQueryKeys.shelfLocations.detail(id),
    queryFn: () => shelfLocationsApi.getById(id).then(res => res.data),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Totals for the staff Locations page (under shelfLocations.all, so every location
 * mutation refreshes it)
 */
export const useLocationSummary = () => {
  return useQuery({
    queryKey: inventoryQueryKeys.shelfLocations.summary(),
    queryFn: () => shelfLocationsApi.getSummary().then(res => res.data),
    staleTime: 60 * 1000,
  });
};

/**
 * Hook to fetch books at a specific location
 */
export const useLocationBooks = (locationId: number, params?: BookCopyQueryParams, enabled = true) => {
  return useQuery({
    queryKey: inventoryQueryKeys.shelfLocations.locationBooks(locationId, params),
    queryFn: () => shelfLocationsApi.getLocationBooks(locationId, params).then(res => res.data),
    enabled: enabled && !!locationId,
    staleTime: 3 * 60 * 1000, // 3 minutes
  });
};

/**
 * Mutation hook to create a new shelf location
 */
export const useCreateShelfLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateShelfLocationData) => 
      shelfLocationsApi.create(data).then(res => res.data),
    onSuccess: (data) => {
      handleMutationSuccess('created shelf location');
      invalidateRelatedQueries(queryClient, 'shelf-location');
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'create shelf location');
    },
  });
};

/**
 * Mutation hook to update a shelf location
 */
export const useUpdateShelfLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateShelfLocationData }) => 
      shelfLocationsApi.update(id, data).then(res => res.data),
    onSuccess: (data, variables) => {
      handleMutationSuccess('updated shelf location');
      invalidateRelatedQueries(queryClient, 'shelf-location', variables.id);
      // Update the specific location in cache
      queryClient.setQueryData(inventoryQueryKeys.shelfLocations.detail(variables.id), data);
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'update shelf location');
    },
  });
};

/**
 * Mutation hook to delete a shelf location
 */
export const useDeleteShelfLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => shelfLocationsApi.delete(id),
    onSuccess: (_, id) => {
      handleMutationSuccess('deleted shelf location');
      // Its copies are now unshelved, so cached copy lists are stale too.
      invalidateRelatedQueries(queryClient, 'shelf-location', id);
      // Remove from cache
      queryClient.removeQueries({ queryKey: inventoryQueryKeys.shelfLocations.detail(id) });
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'delete shelf location');
    },
  });
};

// =============================================================================
// CONDITION HOOKS
// =============================================================================

/**
 * Hook to fetch all conditions
 */
export const useConditions = (params?: BaseQueryParams) => {
  return useQuery({
    queryKey: inventoryQueryKeys.conditions.list(params),
    queryFn: () => conditionsApi.getAll(params).then(res => res.data),
    staleTime: 10 * 60 * 1000, // 10 minutes (conditions change rarely)
  });
};

/**
 * Hook to fetch a specific condition by ID
 */
export const useCondition = (id: number, enabled = true) => {
  return useQuery({
    queryKey: inventoryQueryKeys.conditions.detail(id),
    queryFn: () => conditionsApi.getById(id).then(res => res.data),
    enabled: enabled && !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch condition statistics
 */
export const useConditionStatistics = () => {
  return useQuery({
    queryKey: inventoryQueryKeys.conditions.statistics(),
    queryFn: () => conditionsApi.getStatistics().then(res => res.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mutation hook to create a new condition (staff only)
 */
export const useCreateCondition = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateConditionData) => 
      conditionsApi.create(data).then(res => res.data),
    onSuccess: () => {
      handleMutationSuccess('created condition');
      invalidateRelatedQueries(queryClient, 'condition');
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'create condition');
    },
  });
};

/**
 * Mutation hook to update a condition (staff only)
 */
export const useUpdateCondition = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateConditionData }) => 
      conditionsApi.update(id, data).then(res => res.data),
    onSuccess: (data, variables) => {
      handleMutationSuccess('updated condition');
      invalidateRelatedQueries(queryClient, 'condition');
      // Update specific condition in cache
      queryClient.setQueryData(inventoryQueryKeys.conditions.detail(variables.id), data);
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'update condition');
    },
  });
};

/**
 * Mutation hook to delete a condition (staff only)
 */
export const useDeleteCondition = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => conditionsApi.delete(id),
    onSuccess: (_, id) => {
      handleMutationSuccess('deleted condition');
      invalidateRelatedQueries(queryClient, 'condition');
      // Remove from cache
      queryClient.removeQueries({ queryKey: inventoryQueryKeys.conditions.detail(id) });
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'delete condition');
    },
  });
};

// =============================================================================
// BOOK COPY HOOKS
// =============================================================================

/**
 * Hook to fetch book copies with optional filtering
 */
export const useBookCopies = (params?: BookCopyQueryParams, enabled = true) => {
  return useQuery({
    queryKey: inventoryQueryKeys.bookCopies.list(params),
    queryFn: () => bookCopiesApi.getAll(params).then(res => res.data),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled,
  });
};

/**
 * Hook to fetch a specific book copy by ID
 */
export const useBookCopy = (id: number, enabled = true) => {
  return useQuery({
    queryKey: inventoryQueryKeys.bookCopies.detail(id),
    queryFn: () => bookCopiesApi.getById(id).then(res => res.data),
    enabled: enabled && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to look up a book copy by barcode (for scan-to-find workflows).
 * Only fires when barcode is exactly 12 digits. Never retries on 404.
 */
export const useBookCopyByBarcode = (barcode: string, enabled = true) => {
  return useQuery({
    queryKey: ['inventory', 'bookCopies', 'byBarcode', barcode],
    queryFn: () => bookCopiesApi.getByBarcode(barcode).then(res => res.data),
    enabled: enabled && /^\d{12}$/.test(barcode),
    staleTime: 30 * 1000,
    retry: false,
  });
};

/**
 * Hook to fetch inventory statistics
 */
export const useInventoryStatistics = () => {
  return useQuery({
    queryKey: inventoryQueryKeys.bookCopies.statistics(),
    queryFn: () => bookCopiesApi.getStatistics().then(res => res.data),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch transaction history for a book copy
 */
export const useBookCopyTransactionHistory = (copyId: number, params?: TransactionQueryParams, enabled = true) => {
  return useQuery({
    queryKey: inventoryQueryKeys.bookCopies.transactionHistory(copyId, params),
    queryFn: () => bookCopiesApi.getTransactionHistory(copyId, params).then(res => res.data),
    enabled: enabled && !!copyId,
    staleTime: 3 * 60 * 1000, // 3 minutes
  });
};

/**
 * Mutation hook to create a new book copy
 */
export const useCreateBookCopy = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateBookCopyData) => 
      bookCopiesApi.create(data).then(res => res.data),
    onSuccess: () => {
      handleMutationSuccess('created book copy');
      invalidateRelatedQueries(queryClient, 'book-copy');
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'create book copy');
    },
  });
};

/**
 * Mutation hook to update a book copy
 */
export const useUpdateBookCopy = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBookCopyData }) => 
      bookCopiesApi.update(id, data).then(res => res.data),
    onSuccess: (data, variables) => {
      handleMutationSuccess('updated book copy');
      invalidateRelatedQueries(queryClient, 'book-copy');
      // Update specific copy in cache
      queryClient.setQueryData(inventoryQueryKeys.bookCopies.detail(variables.id), data);
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'update book copy');
    },
  });
};

/**
 * Mutation hook to delete a book copy
 */
export const useDeleteBookCopy = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => bookCopiesApi.delete(id),
    onSuccess: (_, id) => {
      handleMutationSuccess('deleted book copy');
      invalidateRelatedQueries(queryClient, 'book-copy');
      // Remove from cache
      queryClient.removeQueries({ queryKey: inventoryQueryKeys.bookCopies.detail(id) });
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'delete book copy');
    },
  });
};

/**
 * Mutation hook to withdraw a book copy from circulation. Its loans and fines stay on
 * record; the API refuses while the copy is on loan (reason in `error.details.copy`).
 */
export const useWithdrawBookCopy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      bookCopiesApi.withdraw(id, reason),
    onSuccess: (_, { id }) => {
      handleMutationSuccess('withdrew book copy');
      invalidateRelatedQueries(queryClient, 'book-copy');
      queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.bookCopies.detail(id) });
    },
    onError: (error: AxiosError) => {
      const data = error.response?.data as { error?: { details?: { copy?: string[] } } } | undefined;
      const reason = data?.error?.details?.copy?.[0];
      if (reason) toast.error(reason);
      else handleMutationError(error, 'withdraw book copy');
    },
  });
};

/**
 * Mutation hook for bulk location updates
 */
export const useBulkLocationUpdate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: BulkLocationUpdateData) => 
      bookCopiesApi.bulkLocationUpdate(data).then(res => res.data),
    onSuccess: (result) => {
      const updatedCount = result.updated_count ?? 0;
      handleMutationSuccess(`updated locations for ${updatedCount} book copies`);
      invalidateRelatedQueries(queryClient, 'book-copy');
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'bulk update locations');
    },
  });
};

/**
 * Mutation hook for bulk status updates
 */
export const useBulkStatusUpdate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: BulkStatusUpdateData) => 
      bookCopiesApi.bulkStatusUpdate(data).then(res => res.data),
    onSuccess: (result) => {
      const updatedCount = result.updated_count ?? 0;
      handleMutationSuccess(`updated status for ${updatedCount} book copies`);
      invalidateRelatedQueries(queryClient, 'book-copy');
    },
    onError: (error: AxiosError) => {
      handleMutationError(error, 'bulk update statuses');
    },
  });
};

// =============================================================================
// TRANSACTION HOOKS
// =============================================================================

/**
 * Hook to fetch inventory transactions
 */
export const useInventoryTransactions = (params?: TransactionQueryParams) => {
  return useQuery({
    queryKey: inventoryQueryKeys.transactions.list(params),
    queryFn: () => inventoryTransactionsApi.getAll(params).then(res => res.data),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch a specific inventory transaction by ID
 */
export const useInventoryTransaction = (id: number, enabled = true) => {
  return useQuery({
    queryKey: inventoryQueryKeys.transactions.detail(id),
    queryFn: () => inventoryTransactionsApi.getById(id).then(res => res.data),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch activity summary
 */
export const useInventoryActivitySummary = (params?: { days?: number }) => {
  return useQuery({
    queryKey: inventoryQueryKeys.transactions.activitySummary(params),
    queryFn: () => inventoryTransactionsApi.getActivitySummary(params).then(res => res.data),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to prefetch inventory data for improved UX
 */
export const usePrefetchInventoryData = () => {
  const queryClient = useQueryClient();
  
  const prefetchConditions = () => {
    queryClient.prefetchQuery({
      queryKey: inventoryQueryKeys.conditions.list(),
      queryFn: () => conditionsApi.getAll().then(res => res.data),
      staleTime: 10 * 60 * 1000,
    });
  };

  const prefetchShelfLocations = () => {
    queryClient.prefetchQuery({
      queryKey: inventoryQueryKeys.shelfLocations.list(),
      queryFn: () => shelfLocationsApi.getAll().then(res => res.data),
      staleTime: 5 * 60 * 1000,
    });
  };

  const prefetchInventoryStats = () => {
    queryClient.prefetchQuery({
      queryKey: inventoryQueryKeys.bookCopies.statistics(),
      queryFn: () => bookCopiesApi.getStatistics().then(res => res.data),
      staleTime: 1 * 60 * 1000,
    });
  };

  return {
    prefetchConditions,
    prefetchShelfLocations,
    prefetchInventoryStats,
  };
};

/**
 * Hook to get commonly used inventory options
 */
export const useInventoryOptions = () => {
  const { data: conditions } = useConditions();
  const { data: shelfLocations } = useShelfLocations();

  return {
    conditionOptions: conditions?.results?.map(condition => ({
      value: condition.id,
      label: condition.name_display,
      canCirculate: condition.can_be_borrowed,
    })) || [],
    
    locationOptions: shelfLocations?.results?.map(location => ({
      value: location.id,
      label: `${location.label} · ${location.library}`,
    })) || [],
    
    statusOptions: [
      { value: 'available', label: 'Available' },
      { value: 'borrowed', label: 'Borrowed' },
      { value: 'maintenance', label: 'Maintenance' },
      { value: 'missing', label: 'Missing' },
      { value: 'withdrawn', label: 'Withdrawn' },
      { value: 'reserved', label: 'Reserved' },
      { value: 'in_transit', label: 'In Transit' },
    ],
  };
};
